import { Box, Button, Divider, IconButton, OutlinedInput, Tooltip, Typography } from '@mui/material'
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectIfBookmarkingDone, selectIfRemovingBookmarkDone } from '../../store/slices/bookmarksSlice';
import CloseIcon from '@mui/icons-material/Close';

export default function FeedbackElements({
    hideBookmark = false,
    hideCopy = false,
    bookmarked,
    thumbUp,
    bookmarkFn,
    copyFn,
    thumbFn,
}
    : {
        hideBookmark?: boolean,
        hideCopy?: boolean,
        bookmarked?: boolean,
        thumbUp?: boolean | null;
        bookmarkFn?: (value: boolean) => void,
        copyFn?: () => void,
        thumbFn?: (value: boolean, comment?: string) => void,
    }) {

    const [isBookmarked, setIsBookmarked] = useState(bookmarked);
    const [showResponse, setShowResponse] = useState(false);
    const [didThumbUp, setDidThumbUp] = useState(thumbUp === true);

    const [didThumbDown, setDidThumbDown] = useState(thumbUp === false);
    const [comment, setComment] = useState('');
    const [thumbDownSubmitted, setThumbdownSubmitted] = useState(false);

    const creatingBookmarkDone = useSelector(selectIfBookmarkingDone);
    const removingBookmarkDone = useSelector(selectIfRemovingBookmarkDone);
    const disableBookmarkButton = creatingBookmarkDone === false || removingBookmarkDone === false;

    const [showCopiedLabel, setShowCopiedLabel] = useState(false);
    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (showCopiedLabel) {
            timer = setTimeout(() => {
                setShowCopiedLabel(false);
            }, 1500)
        }
        return () => {
            clearTimeout(timer);
        };
    }, [showCopiedLabel]);


    const handleBookmarkClick = () => {
        bookmarkFn && bookmarkFn(!isBookmarked);
        setIsBookmarked(!isBookmarked)
    }

    const handleThumbup = () => {
        setDidThumbUp(true);
        setDidThumbDown(false);
        setShowResponse(true);
        thumbFn && thumbFn(true);
    }

    const handleThumbdown = () => {
        setShowResponse(true);
        setDidThumbDown(true);
    }

    const handleSubmitThumbdown = () => {
        setShowResponse(true);
        setThumbdownSubmitted(true)
        setDidThumbDown(true);
        thumbFn && thumbFn(false, comment);
    }

    return (
        <>
            <Box sx={{
                display: 'inline-flex',
                placeItems: 'center',
                justifyContent: 'right',
                gap: 2,
                '& button': {
                    padding: 0,
                    margin: 0
                },
                '& svg': {
                    opacity: 1,
                    width: 20,
                    fill: 'url(#bookmark_feedback_elements_gradient)',
                    transition: 'opacity 200ms ease-in-out'
                },
                '& svg:hover': {
                    cursor: 'pointer',
                    opacity: 0.8,
                }
            }}>
                {!hideBookmark && <IconButton
                    onClick={handleBookmarkClick}
                    disabled={disableBookmarkButton}
                    sx={{
                        padding: 0,
                        margin: 0,
                        width: '1vw',
                        aspectRatio: 1 / 1,
                        '& > svg': {
                            width: '100%',
                            aspectRatio: 1 / 1,
                        }
                    }}
                >
                    {isBookmarked ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                </IconButton>}
                {!hideCopy && <Tooltip
                    title="Content is copied"
                    open={showCopiedLabel}
                    arrow
                >
                    <IconButton onClick={() => {
                        copyFn && copyFn();
                        setShowCopiedLabel(true);
                    }}
                        sx={{
                            padding: 0,
                            margin: 0,
                            width: '1vw',
                            aspectRatio: 1 / 1,
                            '& > svg': {
                                width: '100%',
                                aspectRatio: 1 / 1,
                            }
                        }}><ContentCopyIcon /></IconButton>
                </Tooltip>}
                {(!hideBookmark || !hideCopy) && <Divider orientation="vertical"
                    sx={{ height: '2vh', borderWidth: '0.15ch', borderColor: 'rgba(0, 0, 0, 0.1)' }} />}
                {
                    (thumbUp === null || thumbUp === true) && <IconButton
                        disabled={thumbUp !== null}
                        onClick={handleThumbup}
                        sx={{
                            padding: 0,
                            margin: 0,
                            width: '1vw',
                            aspectRatio: 1 / 1,
                            '& > svg': {
                                width: '100%',
                                aspectRatio: 1 / 1,
                            }
                        }}>
                        {!didThumbUp && <ThumbUpOffAltIcon />}
                        {didThumbUp && <ThumbUpIcon />}
                    </IconButton>
                }
                {
                    !didThumbUp && <IconButton
                        disabled={thumbUp !== null}
                        onClick={handleThumbdown}
                        sx={{
                            padding: 0,
                            margin: 0,
                            width: '1.25vw',
                            aspectRatio: 1 / 1,
                            '& > svg': {
                                width: '100%',
                                aspectRatio: 1 / 1,
                            }
                        }}>
                        {(!thumbDownSubmitted && thumbUp === null && !didThumbDown) && <ThumbDownOffAltIcon />}
                        {(thumbDownSubmitted || thumbUp === false || didThumbDown) && <ThumbDownIcon />}
                    </IconButton>
                }
            </Box>
            {showResponse && <Box sx={{
                background: '#18FEFE14',
                flexBasis: '100%',
                width: '100%',
                boxSizing: 'border-box',
                padding: 2,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: 4,
                color: 'var(--text-primary)'
            }}>
                {
                    didThumbDown && <Box sx={{ flexBasis: '100%', width: '100%', justifyContent: 'left', gap: 1, }}>
                        {
                            !thumbDownSubmitted && <>
                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'auto 16px'
                                }}>
                                    <Typography variant='subtitle1'>
                                        Thank you for your feedback. Why was this answer not helpful?
                                    </Typography>
                                    <IconButton
                                        disableRipple={true}
                                        onClick={handleSubmitThumbdown}
                                        sx={{
                                            padding: 0,
                                            height: 'max-content',
                                            opacity: 0.9,
                                            transition: 'all 100ms ease-in-out',
                                            width: '1.25vw',
                                            aspectRatio: 1 / 1,
                                            '& > svg': {
                                                width: '100%',
                                                aspectRatio: 1 / 1,
                                            },
                                            '&:hover': {
                                                opacity: 1,
                                                scale: '1.25'
                                            }
                                        }}>
                                        <CloseIcon sx={{ width: 16, aspectRatio: 1 / 1 }} />
                                    </IconButton>
                                </Box>
                                <OutlinedInput
                                    value={comment}
                                    placeholder='Please describe what we could improve...'
                                    onChange={(e: any) => setComment(e.target.value)}
                                    sx={{
                                        width: '100%',
                                        padding: '0 !important',
                                        background: 'var(--text-light)',
                                        border: '1px solid #009CA4 !important',
                                        outline: "none",
                                        borderRadius: 2,
                                        "& fieldset": { border: 'none' },
                                        '&.MuiOutlinedInput-root input': {
                                            paddingX: 2,
                                            paddingY: 1
                                        },
                                        '& ::placeholder': {
                                            color: 'var(--text-dark) !important',
                                            opacity: '0.5 !important'
                                        }
                                    }} />
                                <Button variant='contained'
                                    onClick={handleSubmitThumbdown}
                                    sx={{ marginTop: 1, alignSelf: 'flex-start', color: 'var(--text-light)' }}>Submit</Button></>
                        }
                        {thumbDownSubmitted && <Box sx={{
                            display: 'grid',
                            placeContent: 'center'
                        }}>
                            <Typography variant='subtitle1' textAlign={'center'}>
                                Your feedback has been submitted.
                                <br />
                                Thank you for helping improve AI chat!
                            </Typography>
                        </Box>}
                    </Box>
                }
                {
                    didThumbUp && <Typography variant='subtitle1'>
                        Thank you for your feedback!
                    </Typography>
                }
            </Box>}
        </>
    )
}
